import i18n from 'i18n';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import s from './Settings.module.scss';
import { Modal } from 'components/common/Modal/Modal';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModalNavigationContext } from 'components/pages/ConsultantPage/ConsultantPage';
import { Button } from 'components/common/Button/Button';
import { SelectableOption } from 'components/common/OptionSelector/OptionSelector';
import { OptionRow } from 'components/common/OptionRow/OptionRow';
import { OptionListModal } from 'components/common/OptionListModal/OptionListModal';
import { ScrollableContainer } from 'components/common/ScrollableContainer';
import { ThemeId, useTheme } from 'lib/hooks/useTheme';
import { FONT_SIZE_REM, FontSizeId, useFontSize } from 'lib/hooks/useFontSize';
import {
  selectAppThemes,
  selectNotificationChannels,
  selectUserNewsAndUpdatesChannels,
  selectUserNotificationChannels,
  selectUserTheme,
} from 'store/features/user/selectors';
import { selectLanguages } from 'store/features/settings/selectors';
import { useAppSelector } from 'store/hooks';
import { useUpdateUserSettingsMutation } from 'store/features/user/user-api';
import { useQueryError } from 'lib/hooks/useQueryError';
import { NotificationChannel } from 'types/user';
import { Loader } from 'components/common/Loader/Loader';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';

const fontSizes: SelectableOption[] = [
  {
    id: 'small',
    name: i18n.t('profile.mainSettings.fontSizeToChat.options.small'),
  },
  {
    id: 'standard',
    name: i18n.t('profile.mainSettings.fontSizeToChat.options.standard'),
  },
  {
    id: 'large',
    name: i18n.t('profile.mainSettings.fontSizeToChat.options.large'),
  },
  {
    id: 'extra-large',
    name: i18n.t('profile.mainSettings.fontSizeToChat.options.extraLarge'),
  },
];

const fontSizeIds = fontSizes.map((option) => option.id as FontSizeId);

type SettingsField = 'language' | 'notifications' | 'newsAndUpdates' | 'theme';

type SettingsProps = {};

export const Settings: FC<SettingsProps> = () => {
  const { t } = useTranslation();

  const { closeRouteModal, goBack } =
    useOutletContext<ModalNavigationContext>();

  const notificationChannels = useAppSelector(selectNotificationChannels);
  // const newsAndUpdatesChannels = useAppSelector(
  //   selectNewsAndUpdatesChannels,
  // );
  const themes = useAppSelector(selectAppThemes);

  const languagesData = useAppSelector(selectLanguages);
  const languages = useMemo(
    () =>
      languagesData.map((language) => ({
        id: language.code,
        name: language.name,
      })),
    [languagesData],
  );

  const userNotificationChannels = useAppSelector(
    selectUserNotificationChannels,
  );
  const userNewsAndUpdatesChannels = useAppSelector(
    selectUserNewsAndUpdatesChannels,
  );
  const userTheme = useAppSelector(selectUserTheme);
  const { theme } = useTheme(userTheme);

  const { fontSize: appliedFontSize, setFontSize: commitFontSize } =
    useFontSize();

  const [updateUserSettings, { isLoading, isError, error }] =
    useUpdateUserSettingsMutation();
  const { showError, errorMessage, closeError } = useQueryError(isError, error);

  const allNotificationChannels = useMemo(() => {
    const channels = notificationChannels.map((option) => ({
      id: option.id,
      name: option.name,
    }));
    return [{ id: '', name: t('common.disabled') }, ...channels];
  }, [notificationChannels, t]);

  const [activeField, setActiveField] = useState<SettingsField | null>(null);

  const [langId, setLangId] = useState(i18n.language);
  const lang = useMemo(
    () => languages.find((language) => language.id === langId) ?? null,
    [languages, langId],
  );

  const [fontSizeId, setFontSizeId] = useState<FontSizeId>(appliedFontSize);
  const selectedFontSize = useMemo(
    () => fontSizes.find((option) => option.id === fontSizeId) ?? null,
    [fontSizeId],
  );

  const [notificationId, setNotificationId] = useState<string>(
    userNotificationChannels.join(','),
  );
  const selectedNotification = useMemo(
    () =>
      allNotificationChannels.find((option) => option.id === notificationId) ??
      null,
    [allNotificationChannels, notificationId],
  );

  const [newsAndUpdatesId, setNewsAndUpdatesId] = useState<string>(
    userNewsAndUpdatesChannels.join(','),
  );
  const selectedNewsAndUpdates = useMemo(
    () =>
      allNotificationChannels.find(
        (option) => option.id === newsAndUpdatesId,
      ) ?? null,
    [allNotificationChannels, newsAndUpdatesId],
  );

  const [themeId, setThemeId] = useState(theme);
  const selectedTheme = useMemo(
    () => themes.find((option) => option.id === themeId) ?? null,
    [themes, themeId],
  );

  const isDisabled =
    lang?.id === i18n.language &&
    userNotificationChannels.join(',') === notificationId &&
    userNewsAndUpdatesChannels.join(',') === newsAndUpdatesId &&
    userTheme === themeId &&
    appliedFontSize === fontSizeId;

  const handleChangeFontSize = (e: ChangeEvent<HTMLInputElement>) => {
    setFontSizeId(fontSizeIds[Number(e.target.value)]);
  };

  const handleCloseList = () => {
    setActiveField(null);
  };

  const fields: Record<
    SettingsField,
    {
      title: string;
      options: SelectableOption[];
      selectedId: string;
      onSelect: (id: string) => void;
    }
  > = {
    language: {
      title: t('profile.mainSettings.language'),
      options: languages,
      selectedId: langId,
      onSelect: setLangId,
    },
    notifications: {
      title: t('profile.mainSettings.notifications'),
      options: allNotificationChannels,
      selectedId: notificationId,
      onSelect: setNotificationId,
    },
    newsAndUpdates: {
      title: t('profile.mainSettings.newsAndUpdates'),
      options: allNotificationChannels,
      selectedId: newsAndUpdatesId,
      onSelect: setNewsAndUpdatesId,
    },
    theme: {
      title: t('profile.mainSettings.theme'),
      options: themes,
      selectedId: themeId,
      onSelect: (id) => setThemeId(id as ThemeId),
    },
  };

  const handleSelectOption = (id: string) => {
    if (!activeField) return;

    fields[activeField].onSelect(id);
    setActiveField(null);
  };

  const handleApply = async () => {
    try {
      const isLanguageChanged = lang && lang.id !== i18n.language;
      const isFontSizeChanged = fontSizeId !== appliedFontSize;
      const isNotificationChanged =
        notificationId !== userNotificationChannels.join(',');
      const isNewsAndUpdatesChanged =
        newsAndUpdatesId !== userNewsAndUpdatesChannels.join(',');
      const isThemeChanged = selectedTheme?.id !== userTheme;

      const isSettingsChanged =
        isNotificationChanged || isNewsAndUpdatesChanged || isThemeChanged;

      if (isSettingsChanged) {
        await updateUserSettings({
          notificationChannels: notificationId.split(
            ',',
          ) as NotificationChannel[],
          newsAndUpdatesChannels: newsAndUpdatesId.split(
            ',',
          ) as NotificationChannel[],
          theme: selectedTheme?.id as ThemeId,
        });
      }

      if (isFontSizeChanged) {
        commitFontSize(fontSizeId);
      }

      if (isLanguageChanged) {
        await i18n.changeLanguage(lang.id);
        window.location.reload();
      }
    } catch (applyError) {
      console.error(applyError);
    }
  };

  if (activeField) {
    const { title, options, selectedId } = fields[activeField];

    return (
      <OptionListModal
        title={title}
        options={options}
        selectedId={selectedId}
        onSelect={handleSelectOption}
        onBack={handleCloseList}
        onClose={closeRouteModal}
      />
    );
  }

  return (
    <Modal
      title={t('profile.mainSettings.title')}
      onClose={closeRouteModal}
      onBack={goBack}
    >
      <div className={s['container']}>
        <ScrollableContainer scrollHeight='40rem'>
          <div className={s['settings-container']}>
            <OptionRow
              title={fields.language.title}
              value={lang?.name ?? t('common.notSet')}
              onClick={() => setActiveField('language')}
            />

            <OptionRow
              title={fields.notifications.title}
              value={selectedNotification?.name ?? t('common.notSet')}
              onClick={() => setActiveField('notifications')}
            />

            <OptionRow
              title={fields.newsAndUpdates.title}
              value={selectedNewsAndUpdates?.name ?? t('common.notSet')}
              onClick={() => setActiveField('newsAndUpdates')}
            />

            <OptionRow
              title={fields.theme.title}
              value={selectedTheme?.name ?? t('common.notSet')}
              onClick={() => setActiveField('theme')}
            />

            <div className={s['font-size']}>
              <label htmlFor='font-size' className={s['font-size__title']}>
                {t('profile.mainSettings.fontSizeToChat.title')}
              </label>

              <input
                id='font-size'
                className={s['font-size__slider']}
                type='range'
                min={0}
                max={fontSizeIds.length - 1}
                step={1}
                value={fontSizeIds.indexOf(fontSizeId)}
                onChange={handleChangeFontSize}
                aria-valuetext={selectedFontSize?.name}
              />

              <div className={s['font-size__marks']} aria-hidden='true'>
                {fontSizeIds.map((id) => (
                  <span key={id}>{parseFloat(FONT_SIZE_REM[id]) * 10}</span>
                ))}
              </div>
            </div>
          </div>
        </ScrollableContainer>

        <Button
          className={s['apply-btn']}
          onClick={handleApply}
          isAccent={true}
          isRounded={true}
          disabled={isDisabled}
        >
          {t('common.apply')}
        </Button>
      </div>

      {isLoading && <Loader />}
      {showError && <ErrorModal message={errorMessage} onClose={closeError} />}
    </Modal>
  );
};
